import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Chip, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Typography, Alert } from '@mui/material';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { useUser } from '../../context/userContext';
import { getApplications, reviewApplication, approveApplication, rejectApplication } from '../../api/invitationApi';
import { getAllAgentGroups } from '../../api/userApi';
import { getTiers } from '../../api/tierApi';
import { convertDateTimeToDateString } from '../../utils/datetimeUtils';
import handleAgentName from '../../utils/userUtils';
import AgreementContent from '../public/agreementContent';
import { facetFilterOptions } from '../common/facetFilter';
import { formatIc } from '../../utils/icFormat';

const PP = "'Poppins', sans-serif";
const STATUS_COLORS = {
  PendingReview: { bg: '#FEF3C7', color: '#92400E', label: 'Pending Review' },
  PendingApproval: { bg: '#DBEAFE', color: '#1E40AF', label: 'Pending Final Approval' },
  Approved: { bg: '#D1FAE5', color: '#065F46', label: 'Approved' },
  Rejected: { bg: '#FEE2E2', color: '#991B1B', label: 'Rejected' },
};

export default function AgentApplications({ embedded = false } = {}) {
  const { user } = useUser();
  const [applications, setApplications] = useState([]);
  const [groups, setGroups] = useState([]);
  /* The rates a new agent starts on come from the BASE TIER in salestier, never from
     numbers typed into this page. They were hardcoded as PSC 5 / DOC 1 / TOC 5, so when
     the tiers moved to DOC 3 / TOC 3 on 2026-10-17 this screen carried on prefilling the
     old ones - the table had changed and the approval form had not.
     (user 2026-10-18: "Have you applied the new rate already?") */
  const [baseTier, setBaseTier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reviewDialog, setReviewDialog] = useState(false);
  const [rejectDialog, setRejectDialog] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  const [reviewForm, setReviewForm] = useState({ groupId: '', personalComm: '', doc: '', toc: '' });
  const [rejectReason, setRejectReason] = useState('');
  const [agreementDialog, setAgreementDialog] = useState(false);
  const [agreementApp, setAgreementApp] = useState(null);
  // Bulk review — one dialog, a row per selected agent (PSC/DOC/TOC), submit reviews all at once.
  const [bulkReviewOpen, setBulkReviewOpen] = useState(false);
  const [bulkRows, setBulkRows] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);

  const openAgreement = (app) => {
    setAgreementApp(app);
    setAgreementDialog(true);
  };

  const fetchData = useCallback(async () => {
    try {
      const [appsRes, groupsRes] = await Promise.all([getApplications(), getAllAgentGroups()]);
      setApplications(appsRes.data);
      setGroups(groupsRes.data);
    } catch (err) {
      console.error('Error fetching applications:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    let alive = true;
    getTiers()
      .then((res) => {
        const rows = Array.isArray(res?.data) ? res.data : [];
        // The base tier is the first by SortOrder - the one a new agent joins on.
        if (alive && rows.length) setBaseTier(rows[0]);
      })
      .catch(() => { /* fall back to the figures below */ });
    return () => { alive = false; };
  }, []);

  // Base-tier rates, with the long-standing figures as a last resort if the call fails.
  const defPsc = Number(baseTier?.pscPercent ?? baseTier?.PscPercent ?? 5);
  const defDoc = Number(baseTier?.docPercent ?? baseTier?.DocPercent ?? 1);
  const defToc = Number(baseTier?.tocPercent ?? baseTier?.TocPercent ?? 5);

  const openReview = (app) => {
    setSelectedApp(app);
    // Prefill commission with the base-tier defaults from salestier when
    // the application carries none — the accountant edits before approving if different.
    setReviewForm({
      groupId: app.groupId ?? app.inviterGroupId ?? '',
      personalComm: app.personalComm ?? defPsc,
      doc: app.downlineOverridingComm ?? defDoc,
      toc: app.teamOverridingComm ?? defToc,
    });
    setReviewDialog(true);
  };

  const handleReview = async () => {
    try {
      await reviewApplication({
        applicationId: selectedApp.applicationId,
        groupId: parseInt(reviewForm.groupId),
        personalComm: parseFloat(reviewForm.personalComm) || 0,
        downlineOverridingComm: reviewForm.doc !== '' ? parseFloat(reviewForm.doc) : null,
        teamOverridingComm: reviewForm.toc !== '' ? parseFloat(reviewForm.toc) : null,
        reviewedBy: user.username,
      });
      setReviewDialog(false);
      fetchData();
    } catch (err) {
      alert('Failed: ' + (err.response?.data || err.message));
    }
  };

  const handleApprove = async (appId) => {
    if (!window.confirm('Approve this application? This will activate the agent account.')) return;
    try {
      await approveApplication(appId, user.username);
      fetchData();
      alert('Agent activated.');
    } catch (err) {
      alert('Failed: ' + (err.response?.data || err.message));
    }
  };

  // ── Bulk review (step 1): fill each selected agent's rates in one dialog, submit all ──
  const openBulkReview = (rows) => {
    setBulkRows(rows.map(a => ({
      applicationId: a.applicationId,
      name: handleAgentName(a.firstName, a.lastName, a.alias),
      groupId: a.groupId ?? a.inviterGroupId ?? '',
      groupName: a.groupName ?? a.inviterGroupName ?? '—',
      personalComm: a.personalComm ?? defPsc,
      doc: a.downlineOverridingComm ?? defDoc,
      toc: a.teamOverridingComm ?? defToc,
    })));
    setBulkReviewOpen(true);
  };
  const setBulkField = (id, field, val) =>
    setBulkRows(rows => rows.map(r => r.applicationId === id ? { ...r, [field]: val } : r));
  const submitBulkReview = async () => {
    const bad = bulkRows.find(r => r.personalComm === '' || isNaN(parseFloat(r.personalComm)) || !r.groupId);
    if (bad) { alert(`Every agent needs a valid PSC % and a group — check "${bad.name}".`); return; }
    setBulkBusy(true);
    let ok = 0; let failed = 0;
    for (const r of bulkRows) {
      try {
        await reviewApplication({
          applicationId: r.applicationId,
          groupId: parseInt(r.groupId),
          personalComm: parseFloat(r.personalComm) || 0,
          downlineOverridingComm: r.doc !== '' ? parseFloat(r.doc) : null,
          teamOverridingComm: r.toc !== '' ? parseFloat(r.toc) : null,
          reviewedBy: user.username,
        });
        ok++;
      } catch { failed++; }
    }
    setBulkBusy(false);
    setBulkReviewOpen(false);
    fetchData();
    alert(`Reviewed ${ok} of ${bulkRows.length}.${failed ? ` ${failed} failed.` : ''} They now await final approval.`);
  };

  // ── Bulk approve (step 2): activate the selected already-reviewed agents ──
  const bulkApprove = async (rows) => {
    if (!rows.length) return;
    if (!window.confirm(`Approve ${rows.length} agent application(s)? This activates their accounts.`)) return;
    let ok = 0; let failed = 0;
    for (const a of rows) { try { await approveApplication(a.applicationId, user.username); ok++; } catch { failed++; } }
    fetchData();
    alert(`Activated ${ok} of ${rows.length}.${failed ? ` ${failed} failed.` : ''}`);
  };

  const openReject = (app) => {
    setSelectedApp(app);
    setRejectReason('');
    setRejectDialog(true);
  };

  const handleReject = async () => {
    try {
      await rejectApplication({
        applicationId: selectedApp.applicationId,
        reason: rejectReason,
        rejectedBy: user.username,
      });
      setRejectDialog(false);
      fetchData();
    } catch (err) {
      alert('Failed: ' + (err.response?.data || err.message));
    }
  };

  const canReview = user?.roleRights?.includes('ReviewAgentApplication');
  const canApprove = user?.roleRights?.includes('ApproveAgentApplication');

  const pendingReview = useMemo(() => applications.filter(a => a.status === 'PendingReview'), [applications]);
  const pendingApproval = useMemo(() => applications.filter(a => a.status === 'PendingApproval'), [applications]);
  const finalised = useMemo(() => applications.filter(a => a.status === 'Approved' || a.status === 'Rejected'), [applications]);

  const columns = useMemo(() => [
    { id: 'id', header: 'ID', accessorKey: 'applicationId', size: 50 },
    { id: 'name', header: 'Name', accessorFn: row => handleAgentName(row.firstName, row.lastName, row.alias), size: 130 },
    { id: 'ic', header: 'IC', accessorKey: 'icNumber', size: 120,
      Cell: ({ cell }) => formatIc(cell.getValue()) },
    { id: 'phone', header: 'Phone', accessorKey: 'phoneNumber', size: 110 },
    { id: 'email', header: 'Email', accessorFn: row => row.email || '—', size: 140 },
    { id: 'username', header: 'Username', accessorKey: 'username', size: 100 },
    { id: 'inviter', header: 'Invited By', accessorKey: 'inviterName', size: 120 },
    { id: 'group', header: 'Group', accessorFn: row => row.groupName ?? '—', size: 90 },
    { id: 'psc', header: 'PSC', accessorFn: row => row.personalComm != null ? `${row.personalComm}%` : '—', size: 55 },
    { id: 'doc', header: 'DOC', accessorFn: row => row.downlineOverridingComm != null ? `${row.downlineOverridingComm}%` : '—', size: 55 },
    { id: 'toc', header: 'TOC', accessorFn: row => row.teamOverridingComm != null ? `${row.teamOverridingComm}%` : '—', size: 55 },
    {
      id: 'status', header: 'Status', accessorKey: 'status', size: 120,
      Cell: ({ cell }) => {
        const val = cell.getValue();
        const s = STATUS_COLORS[val] ?? { bg: '#F1F5F9', color: '#64748B', label: val };
        return <Chip label={s.label} size="small" sx={{ backgroundColor: s.bg, color: s.color, fontWeight: 600, fontSize: '0.65rem', fontFamily: PP }} />;
      },
    },
    {
      id: 'created', header: 'Applied', size: 90,
      Cell: ({ row }) => convertDateTimeToDateString(row.original.createdOn),
    },
    {
      id: 'actions', header: '', size: 240, enableSorting: false, enableColumnFilter: false,
      Cell: ({ row }) => {
        const app = row.original;
        return (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <span onClick={(e) => { e.stopPropagation(); openAgreement(app); }}
              style={{ color: '#64748B', cursor: 'pointer', fontWeight: 500, fontSize: '0.75rem', fontFamily: PP, textDecoration: 'underline' }}>View Agreement</span>
            {app.status === 'PendingReview' && canReview && (
              <>
                <span onClick={(e) => { e.stopPropagation(); openReview(app); }}
                  style={{ color: '#1A8CFF', cursor: 'pointer', fontWeight: 500, fontSize: '0.75rem', fontFamily: PP }}>Review &amp; Submit</span>
                <span onClick={(e) => { e.stopPropagation(); openReject(app); }}
                  style={{ color: '#EF4444', cursor: 'pointer', fontWeight: 500, fontSize: '0.75rem', fontFamily: PP }}>Reject</span>
              </>
            )}
            {app.status === 'PendingApproval' && canApprove && (
              <>
                <span onClick={(e) => { e.stopPropagation(); handleApprove(app.applicationId); }}
                  style={{ color: '#10B981', cursor: 'pointer', fontWeight: 600, fontSize: '0.75rem', fontFamily: PP }}>Approve</span>
                <span onClick={(e) => { e.stopPropagation(); openReject(app); }}
                  style={{ color: '#EF4444', cursor: 'pointer', fontWeight: 500, fontSize: '0.75rem', fontFamily: PP }}>Reject</span>
              </>
            )}
            {app.status === 'PendingApproval' && !canApprove && (
              <span style={{ color: '#64748B', fontSize: '0.75rem', fontFamily: PP }}>Awaiting Superadmin…</span>
            )}
            {app.status === 'Approved' && (
              <Chip label="Active" size="small" sx={{ backgroundColor: '#D1FAE5', color: '#065F46', fontWeight: 600, fontSize: '0.65rem' }} />
            )}
            {app.status === 'Rejected' && (
              <Chip label="Rejected" size="small" sx={{ backgroundColor: '#FEE2E2', color: '#991B1B', fontWeight: 600, fontSize: '0.65rem' }} />
            )}
          </Box>
        );
      },
    },
  ], [canReview, canApprove]);

  const reviewTable = useMaterialReactTable({
        // Excel-style column filters: a search box over a tick-box list of the values
        // actually in that column, for every column at once. A column with nearly one
        // value per row falls back to the plain box on its own.
        // (user 2026-10-06: "for all table maybe yes. go ahead with this method")
        ...facetFilterOptions,
    columns, data: pendingReview,
    enableRowSelection: canReview, positionToolbarAlertBanner: 'none',
    getRowId: (row, i) => String(row.applicationId ?? i),
    renderTopToolbarCustomActions: ({ table }) => {
      const sel = table.getSelectedRowModel().rows;
      return (canReview && sel.length)
        ? <Button size="small" variant="contained" onClick={() => openBulkReview(sel.map(r => r.original))} sx={{ textTransform: 'none', fontFamily: PP, fontSize: '0.78rem', fontWeight: 600, borderRadius: '8px' }}>Review Selected ({sel.length})</Button>
        : null;
    },
    enableColumnActions: false, enableColumnDragging: false,
    enableDensityToggle: false, enableFullScreenToggle: false, enableHiding: false,
    enableStickyHeader: true, muiTableContainerProps: { sx: { maxHeight: 'unset' } },
    initialState: { density: 'compact', pagination: { pageSize: 25 } },
    muiTablePaperProps: { elevation: 0, sx: { border: 'none', backgroundColor: 'transparent', borderRadius: '14px' } },
    muiTableHeadCellProps: { sx: { backgroundColor: '#F8FAFC', borderBottom: '2px solid #E2E8F0', textTransform: 'uppercase', fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.5px', color: '#64748B', fontFamily: PP, padding: '8px 10px' } },
    muiTableBodyCellProps: { sx: { fontSize: '0.8rem', color: '#374151', borderBottom: '1px solid #F1F5F9', fontFamily: PP, padding: '6px 10px' } },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: (e) => {
        if (e.target.tagName === 'SPAN' && e.target.style.cursor === 'pointer') return;
        if (canReview) openReview(row.original);
      },
      sx: { cursor: canReview ? 'pointer' : 'default', '&:hover': { backgroundColor: '#F0F7FF', transition: 'background-color 0.12s' } },
    }),
    muiTopToolbarProps: { sx: { backgroundColor: '#FFFFFF', borderBottom: '1px solid #E2E8F0', minHeight: '48px' } },
    muiBottomToolbarProps: { sx: { backgroundColor: '#FFFFFF', borderTop: '1px solid #E2E8F0' } },
    muiSearchTextFieldProps: { placeholder: 'Search…', sx: { '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#F8FAFC', fontSize: '0.8rem', fontFamily: PP } } },
    muiPaginationProps: { shape: 'rounded', showRowsPerPage: true, rowsPerPageOptions: [10, 25, 50] },
  });

  const approvalTable = useMaterialReactTable({
        // Excel-style column filters: a search box over a tick-box list of the values
        // actually in that column, for every column at once. A column with nearly one
        // value per row falls back to the plain box on its own.
        // (user 2026-10-06: "for all table maybe yes. go ahead with this method")
        ...facetFilterOptions,
    columns, data: pendingApproval,
    enableRowSelection: canApprove, positionToolbarAlertBanner: 'none',
    getRowId: (row, i) => String(row.applicationId ?? i),
    renderTopToolbarCustomActions: ({ table }) => {
      const sel = table.getSelectedRowModel().rows;
      return (canApprove && sel.length)
        ? <Button size="small" variant="contained" color="success" onClick={() => { bulkApprove(sel.map(r => r.original)); table.resetRowSelection(); }} sx={{ textTransform: 'none', fontFamily: PP, fontSize: '0.78rem', fontWeight: 600, borderRadius: '8px' }}>Approve Selected ({sel.length})</Button>
        : null;
    },
    enableColumnActions: false, enableColumnDragging: false,
    enableDensityToggle: false, enableFullScreenToggle: false, enableHiding: false,
    enableStickyHeader: true, muiTableContainerProps: { sx: { maxHeight: 'unset' } },
    initialState: { density: 'compact', pagination: { pageSize: 25 } },
    muiTablePaperProps: { elevation: 0, sx: { border: 'none', backgroundColor: 'transparent', borderRadius: '14px' } },
    muiTableHeadCellProps: { sx: { backgroundColor: '#F8FAFC', borderBottom: '2px solid #E2E8F0', textTransform: 'uppercase', fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.5px', color: '#64748B', fontFamily: PP, padding: '8px 10px' } },
    muiTableBodyCellProps: { sx: { fontSize: '0.8rem', color: '#374151', borderBottom: '1px solid #F1F5F9', fontFamily: PP, padding: '6px 10px' } },
    muiTopToolbarProps: { sx: { backgroundColor: '#FFFFFF', borderBottom: '1px solid #E2E8F0', minHeight: '48px' } },
    muiBottomToolbarProps: { sx: { backgroundColor: '#FFFFFF', borderTop: '1px solid #E2E8F0' } },
    muiSearchTextFieldProps: { placeholder: 'Search…', sx: { '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#F8FAFC', fontSize: '0.8rem', fontFamily: PP } } },
    muiPaginationProps: { shape: 'rounded', showRowsPerPage: true, rowsPerPageOptions: [10, 25, 50] },
  });

  const historyTable = useMaterialReactTable({
        // Excel-style column filters: a search box over a tick-box list of the values
        // actually in that column, for every column at once. A column with nearly one
        // value per row falls back to the plain box on its own.
        // (user 2026-10-06: "for all table maybe yes. go ahead with this method")
        ...facetFilterOptions,
    columns, data: finalised,
    enableColumnActions: false, enableColumnDragging: false,
    enableDensityToggle: false, enableFullScreenToggle: false, enableHiding: false,
    enableStickyHeader: true, muiTableContainerProps: { sx: { maxHeight: 'unset' } },
    initialState: { density: 'compact', pagination: { pageSize: 25 } },
    muiTablePaperProps: { elevation: 0, sx: { border: 'none', backgroundColor: 'transparent', borderRadius: '14px' } },
    muiTableHeadCellProps: { sx: { backgroundColor: '#F8FAFC', borderBottom: '2px solid #E2E8F0', textTransform: 'uppercase', fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.5px', color: '#64748B', fontFamily: PP, padding: '8px 10px' } },
    muiTableBodyCellProps: { sx: { fontSize: '0.8rem', color: '#374151', borderBottom: '1px solid #F1F5F9', fontFamily: PP, padding: '6px 10px' } },
    muiTopToolbarProps: { sx: { backgroundColor: '#FFFFFF', borderBottom: '1px solid #E2E8F0', minHeight: '48px' } },
    muiBottomToolbarProps: { sx: { backgroundColor: '#FFFFFF', borderTop: '1px solid #E2E8F0' } },
    muiSearchTextFieldProps: { placeholder: 'Search…', sx: { '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#F8FAFC', fontSize: '0.8rem', fontFamily: PP } } },
    muiPaginationProps: { shape: 'rounded', showRowsPerPage: true, rowsPerPageOptions: [10, 25, 50] },
  });

  if (loading) return <div>Loading...</div>;

  return (
    <div>
      {!embedded && (
        <Box sx={{ mb: 2 }}>
          <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#0F172A', fontFamily: PP }}>Agent Applications</h2>
          <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748B', fontFamily: PP }}>Review and approve new agent applications</p>
        </Box>
      )}

      {canReview && (
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#0F172A', fontFamily: PP, mb: 1 }}>
            Pending Review ({pendingReview.length})
          </Typography>
          <div style={{ backgroundColor: '#fff', borderRadius: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
            <MaterialReactTable table={reviewTable} />
          </div>
        </Box>
      )}

      {canApprove && (
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#0F172A', fontFamily: PP, mb: 1 }}>
            Pending Final Approval ({pendingApproval.length})
          </Typography>
          <div style={{ backgroundColor: '#fff', borderRadius: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
            <MaterialReactTable table={approvalTable} />
          </div>
        </Box>
      )}

      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#0F172A', fontFamily: PP, mb: 1 }}>
          History ({finalised.length})
        </Typography>
        <div style={{ backgroundColor: '#fff', borderRadius: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
          <MaterialReactTable table={historyTable} />
        </div>
      </Box>

      {/* Review Dialog */}
      <Dialog open={reviewDialog} onClose={() => setReviewDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '14px' } }}>
        <DialogTitle sx={{ fontFamily: PP, fontWeight: 700, fontSize: '1.1rem' }}>
          Review Application — {handleAgentName(selectedApp?.firstName, selectedApp?.lastName, selectedApp?.alias)}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
          <Alert severity="info" sx={{ borderRadius: '8px', fontFamily: PP }}>
            Set the commission rates, then submit. The applicant's account will be created (still inactive)
            and queued for Superadmin's final approval.
          </Alert>
          <Box>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: '#374151', mb: 0.5, fontFamily: PP }}>
              Upline
            </Typography>
            <TextField size="small" fullWidth value={selectedApp?.inviterName || '—'} disabled
              InputProps={{ sx: { fontFamily: PP, fontSize: '0.85rem', borderRadius: '8px', backgroundColor: '#F1F5F9' } }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: '#374151', mb: 0.5, fontFamily: PP }}>
              Group
            </Typography>
            <TextField size="small" fullWidth value={selectedApp?.inviterGroupName || '—'} disabled
              InputProps={{ sx: { fontFamily: PP, fontSize: '0.85rem', borderRadius: '8px', backgroundColor: '#F1F5F9' } }} />
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: '#374151', mb: 0.5, fontFamily: PP }}>PSC %</Typography>
              <TextField size="small" fullWidth type="number" value={reviewForm.personalComm} onChange={(e) => setReviewForm(prev => ({ ...prev, personalComm: e.target.value }))}
                InputProps={{ sx: { fontFamily: PP, fontSize: '0.85rem', borderRadius: '8px', backgroundColor: '#FAFAFA' } }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: '#374151', mb: 0.5, fontFamily: PP }}>DOC %</Typography>
              <TextField size="small" fullWidth type="number" value={reviewForm.doc} onChange={(e) => setReviewForm(prev => ({ ...prev, doc: e.target.value }))}
                InputProps={{ sx: { fontFamily: PP, fontSize: '0.85rem', borderRadius: '8px', backgroundColor: '#FAFAFA' } }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: '#374151', mb: 0.5, fontFamily: PP }}>TOC %</Typography>
              <TextField size="small" fullWidth type="number" value={reviewForm.toc} onChange={(e) => setReviewForm(prev => ({ ...prev, toc: e.target.value }))}
                InputProps={{ sx: { fontFamily: PP, fontSize: '0.85rem', borderRadius: '8px', backgroundColor: '#FAFAFA' } }} />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setReviewDialog(false)} sx={{ fontFamily: PP, textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={handleReview} sx={{
            background: 'linear-gradient(135deg, #1A8CFF, #0F62CC)', fontFamily: PP, fontWeight: 600,
            textTransform: 'none', borderRadius: '8px', boxShadow: 'none',
            '&:hover': { background: 'linear-gradient(135deg, #0F62CC, #0A4FA3)', boxShadow: 'none' },
          }}>Submit for Approval</Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Review Dialog — a row per selected agent; set each PSC/DOC/TOC, submit all at once */}
      <Dialog open={bulkReviewOpen} onClose={() => !bulkBusy && setBulkReviewOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '14px' } }}>
        <DialogTitle sx={{ fontFamily: PP, fontWeight: 700, fontSize: '1.1rem' }}>
          Bulk Review — {bulkRows.length} agent{bulkRows.length === 1 ? '' : 's'}
        </DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          <Alert severity="info" sx={{ borderRadius: '8px', fontFamily: PP, mb: 1.5 }}>
            Set each agent's commission rates, then submit — all move to <b>Pending Final Approval</b> together. Defaults prefilled from the base tier (PSC {defPsc} / DOC {defDoc} / TOC {defToc}); edit any before submitting.
          </Alert>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr 0.8fr 0.8fr 0.8fr', gap: 1, px: 0.5, mb: 0.3 }}>
            {['Agent', 'Group', 'PSC %', 'DOC %', 'TOC %'].map(h => (
              <Typography key={h} sx={{ fontSize: '0.66rem', fontWeight: 700, color: '#64748B', fontFamily: PP, textTransform: 'uppercase', letterSpacing: '0.4px' }}>{h}</Typography>
            ))}
          </Box>
          <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
            {bulkRows.map(r => (
              <Box key={r.applicationId} sx={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr 0.8fr 0.8fr 0.8fr', gap: 1, alignItems: 'center', py: 0.5, borderTop: '1px solid #F1F5F9' }}>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#0F172A', fontFamily: PP }}>{r.name}</Typography>
                <Typography sx={{ fontSize: '0.74rem', color: '#64748B', fontFamily: PP }}>{r.groupName}</Typography>
                <TextField size="small" type="number" value={r.personalComm} onChange={e => setBulkField(r.applicationId, 'personalComm', e.target.value)} onWheel={e => e.target.blur()} InputProps={{ sx: { fontFamily: PP, fontSize: '0.8rem' } }} inputProps={{ step: 0.5, min: 0 }} />
                <TextField size="small" type="number" value={r.doc} onChange={e => setBulkField(r.applicationId, 'doc', e.target.value)} onWheel={e => e.target.blur()} InputProps={{ sx: { fontFamily: PP, fontSize: '0.8rem' } }} inputProps={{ step: 0.5, min: 0 }} />
                <TextField size="small" type="number" value={r.toc} onChange={e => setBulkField(r.applicationId, 'toc', e.target.value)} onWheel={e => e.target.blur()} InputProps={{ sx: { fontFamily: PP, fontSize: '0.8rem' } }} inputProps={{ step: 0.5, min: 0 }} />
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button disabled={bulkBusy} onClick={() => setBulkReviewOpen(false)} sx={{ fontFamily: PP, textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" disabled={bulkBusy} onClick={submitBulkReview} sx={{ background: 'linear-gradient(135deg, #1A8CFF, #0F62CC)', fontFamily: PP, fontWeight: 600, textTransform: 'none', borderRadius: '8px', boxShadow: 'none', '&:hover': { background: 'linear-gradient(135deg, #0F62CC, #0A4FA3)', boxShadow: 'none' } }}>
            {bulkBusy ? 'Submitting…' : `Submit ${bulkRows.length} for Approval`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialog} onClose={() => setRejectDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '14px' } }}>
        <DialogTitle sx={{ fontFamily: PP, fontWeight: 700, fontSize: '1.1rem', color: '#991B1B' }}>
          Reject Application
        </DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: '#374151', mb: 0.5, fontFamily: PP }}>Reason for rejection</Typography>
          <TextField size="small" fullWidth multiline minRows={3} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Enter reason..." InputProps={{ sx: { fontFamily: PP, fontSize: '0.85rem', borderRadius: '8px', backgroundColor: '#FAFAFA' } }} />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setRejectDialog(false)} sx={{ fontFamily: PP, textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleReject} sx={{ fontFamily: PP, fontWeight: 600, textTransform: 'none', borderRadius: '8px', boxShadow: 'none' }}>
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Agreement Dialog */}
      <Dialog open={agreementDialog} onClose={() => setAgreementDialog(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '14px' } }}>
        <DialogTitle sx={{ fontFamily: PP, fontWeight: 700, fontSize: '1.1rem' }}>
          Agreement Preview
        </DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          {agreementApp && (
            <AgreementContent data={{
              fullName: handleAgentName(agreementApp.firstName, agreementApp.lastName, agreementApp.alias) || '—',
              icNumber: agreementApp.icNumber,
              address: agreementApp.address,
              groupName: agreementApp.groupName,
              effectiveDate: convertDateTimeToDateString(agreementApp.approvedOn || agreementApp.createdOn),
              personalComm: agreementApp.personalComm,
              downlineOverridingComm: agreementApp.downlineOverridingComm,
              roles: [agreementApp.intendedRole || 'Sales Agent'],
            }} />
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setAgreementDialog(false)} sx={{ fontFamily: PP, textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
